import React, { ReactNode, useCallback, useEffect, useState } from 'react'

import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'

import SidebarDialog from 'components_new/molecules/SidebarDialog'

import { useTranslation } from 'language/translator'
import { capitalizeFirstLetter } from 'helpers/Functions'

interface DeleteDialogProps {
  children?: ReactNode
  onClose: () => void
  onDelete: (callback: () => void) => void
  open: boolean
  title?: ReactNode | string
  type: string
  unlockPhrase?: string
}

const DeleteDialog = (props: DeleteDialogProps) => {
  const {
    children,
    onClose,
    onDelete,
    open,
    title = '',
    type,
    unlockPhrase
  } = props
  const [loading, setLoading] = useState(false)
  const [input, setInput] = useState('')

  const { t } = useTranslation()

  const handleClose = () => {
    setInput('')
    setLoading(false)
    onClose()
  }

  const handleDelete = useCallback(() => {
    setLoading(true)
    onDelete(() => setLoading(false))
  }, [onDelete])

  const disabled = Boolean(unlockPhrase) && input !== unlockPhrase

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !disabled) {
        handleDelete()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, disabled, handleDelete])

  return (
    <ThemeProvider theme={getTheme('light')}>
      <SidebarDialog
        actions={
          <>
            <Button variant="text" onClick={handleClose}>
              {t('global', 'cancel')}
            </Button>
            <Button
              color="error"
              disabled={disabled}
              loading={loading}
              onClick={handleDelete}
            >
              {`${t('global', 'delete')} ${type}`}
            </Button>
          </>
        }
        maxWidth="xs"
        open={open}
        onClose={onClose}
        title={`${t('global', 'delete')} ${type}?`}
      >
        <Text color="text.primary">
          {title ? (
            <>{typeof title === 'string' ? <b>{title}</b> : title} </>
          ) : (
            `${capitalizeFirstLetter(type)} `
          )}
          {t('global', 'will_be_deleted_are_you_sure')}
        </Text>
        {children ? <Box sx={{ mt: 2 }}>{children}</Box> : null}
        {unlockPhrase ? (
          <TextField
            label={
              <>
                {t('global', 'type_write')} <b>{unlockPhrase}</b>{' '}
                {t('global', 'to_delete')} {type}
              </>
            }
            onChange={(event) => setInput(event.target.value)}
            placeholder={unlockPhrase}
            sx={{ mt: 2 }}
          />
        ) : null}
      </SidebarDialog>
    </ThemeProvider>
  )
}

export default DeleteDialog
